import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/* ================= TYPES ================= */

export type CartItem = {
  productId: string;
  ownerId: string;
  name: string;
  price: number;        // EUR, per unit
  lineTotal?: number;   // price * qty, re-derived on every change
  photo?: string;
  qty: number;
  color?: string;       // each color + size is its own line
  size?: string;
  stock?: number;       // caps qty
  unit?: string;
  categoryId?: number;
};

/** the shop sends `quantity`, older callers send `qty`; both work */
export type AddToCartPayload = Omit<CartItem, "qty"> & { qty?: number; quantity?: number };

export type VariantKey = { productId: string; color?: string; size?: string };

export type CartState = {
  userId: string | null;
  items: CartItem[];
};

/* ================= STATE ================= */

const KEY = "afruity_cart";
const loadItems = (): CartItem[] => {
  try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; }
};
export const persistCart = (items: CartItem[]) => {
  try { localStorage.setItem(KEY, JSON.stringify(items)); } catch {}
};

const initialState: CartState = { userId: null, items: loadItems() };

const line = (i: CartItem) => +(i.price * i.qty).toFixed(2);

/** same product AND same color AND same size */
const same = (i: CartItem, k: VariantKey) =>
  i.productId === k.productId && (i.color ?? "") === (k.color ?? "") && (i.size ?? "") === (k.size ?? "");

/* ================= SLICE ================= */

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    setCartUser: (state, action: PayloadAction<string | null>) => { state.userId = action.payload; },

    /* ===== ADD TO CART ===== */
    addToCart: (state, action: PayloadAction<AddToCartPayload>) => {
      const { qty: _q, quantity: _n, ...rest } = action.payload;
      const qtyToAdd = Math.max(1, Number(action.payload.quantity ?? action.payload.qty) || 1);
      const existing = state.items.find((i) => same(i, rest));
      if (existing) {
        const cap = existing.stock ?? Infinity;
        existing.qty = Math.min(existing.qty + qtyToAdd, cap);
        existing.lineTotal = line(existing);
      } else {
        const cap = rest.stock ?? Infinity;
        const item: CartItem = { ...rest, qty: Math.min(qtyToAdd, cap) };
        item.lineTotal = line(item);
        if (item.qty > 0) state.items.push(item);
      }
    },

    /* ===== DECREMENT (one color + size line) ===== */
    decrementCartItem: (state, action: PayloadAction<VariantKey>) => {
      const item = state.items.find((i) => same(i, action.payload));
      if (!item) return;
      if (item.qty > 1) {
        item.qty -= 1;
        item.lineTotal = line(item);
      } else {
        state.items = state.items.filter((i) => !same(i, action.payload));
      }
    },

    /* ===== SET QTY (one color + size line) ===== */
    setItemQty: (state, action: PayloadAction<VariantKey & { qty: number }>) => {
      const item = state.items.find((i) => same(i, action.payload));
      if (!item) return;
      const cap = item.stock ?? Infinity;
      item.qty = Math.max(1, Math.min(Math.floor(action.payload.qty) || 1, cap));
      item.lineTotal = line(item);
    },

    /* ===== REMOVE (one color + size line) ===== */
    removeFromCart: (state, action: PayloadAction<VariantKey>) => {
      state.items = state.items.filter((i) => !same(i, action.payload));
    },

    clearCart: (state) => { state.items = []; },
  },
});

export const { setCartUser, addToCart, decrementCartItem, setItemQty, removeFromCart, clearCart } = cartSlice.actions;

export const cartCount = (items: CartItem[]) => items.reduce((n, i) => n + i.qty, 0);
export const cartSubtotal = (items: CartItem[]) => +items.reduce((t, i) => t + i.price * i.qty, 0).toFixed(2);

export default cartSlice.reducer;